import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  TextField,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Delete, Edit, Refresh, MoreTime, EditCalendar } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getAllBookings, deleteBooking } from '../services/bookingService';
import { deleteSeries, extendSeries, previewExtendSeries } from '../services/seriesService';
import ModifySeriesDialog from './ModifySeriesDialog';
import { format, addMonths } from 'date-fns';

const formatDateUK = (dateString) => {
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

const getDayOfWeek = (dateString) => {
  try {
    return format(new Date(dateString), 'EEEE');
  } catch {
    return '-';
  }
};

const CANCEL_REASONS = [
  'Clinic Cancelled',
  'Meeting Cancelled',
  'Sickness',
  'Annual Leave',
  'Meeting/Clinic Changed to Virtual',
  'Room/Desk No Longer Required',
  'Other',
];

function BookingList() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filters, setFilters] = useState({
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: null,
    resource_type: '',
    status: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Cancel dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Extend dialog
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [bookingToExtend, setBookingToExtend] = useState(null);
  const [extendEndDate, setExtendEndDate] = useState(null);
  const [extendError, setExtendError] = useState('');
  const [extendLoading, setExtendLoading] = useState(false);
  const [extendPreview, setExtendPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Modify dialog
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
  const [bookingToModify, setBookingToModify] = useState(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status) params.status = filters.status;
      const data = await getAllBookings(params);
      setBookings(data);
      setPage(0);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  // ── Cancel ────────────────────────────────────────────────────────────────

  const handleDeleteClick = (booking) => {
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSingle = async () => {
    if (bookingToDelete) {
      try {
        await deleteBooking(bookingToDelete.id, cancelReason);
        loadBookings();
      } catch (error) {
        console.error('Failed to cancel booking:', error);
      }
    }
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
    setCancelReason('');
  };

  const handleDeleteSeries = async () => {
    if (bookingToDelete?.series_id) {
      try {
        await deleteSeries(bookingToDelete.series_id, bookingToDelete.booking_date, cancelReason);
        loadBookings();
      } catch (error) {
        console.error('Failed to cancel series:', error);
      }
    }
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
    setCancelReason('');
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
    setCancelReason('');
  };

  // ── Edit / Modify / Extend ────────────────────────────────────────────────

  const handleEdit = (booking) => navigate(`/bookings/edit/${booking.id}`);

  const handleModifyClick = (booking) => {
    setBookingToModify(booking);
    setModifyDialogOpen(true);
  };

  const handleExtendClick = (booking) => {
    setBookingToExtend(booking);
    setExtendEndDate(addMonths(new Date(), 12));
    setExtendError('');
    setExtendPreview(null);
    setExtendDialogOpen(true);
  };

  const handleExtendPreview = async () => {
    if (!extendEndDate) { setExtendError('Please select a new end date'); return; }
    setPreviewLoading(true);
    setExtendError('');
    try {
      const preview = await previewExtendSeries(bookingToExtend.series_id, format(extendEndDate, 'yyyy-MM-dd'));
      setExtendPreview(preview);
    } catch (error) {
      setExtendError(error.response?.data?.error || 'Failed to preview extension');
      setExtendPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExtendConfirm = async () => {
    if (!extendEndDate || !extendPreview) return;
    setExtendLoading(true);
    setExtendError('');
    try {
      await extendSeries(bookingToExtend.series_id, format(extendEndDate, 'yyyy-MM-dd'));
      setExtendDialogOpen(false);
      setBookingToExtend(null);
      setExtendEndDate(null);
      setExtendPreview(null);
      loadBookings();
    } catch (error) {
      setExtendError(error.response?.data?.error || 'Failed to extend series');
    } finally {
      setExtendLoading(false);
    }
  };

  const handleExtendCancel = () => {
    setExtendDialogOpen(false);
    setBookingToExtend(null);
    setExtendEndDate(null);
    setExtendError('');
    setExtendPreview(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      case 'completed': return 'default';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>View Bookings</Typography>
      </Box>

      {/* ── Filters ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <DatePicker
              label="Start Date"
              value={filters.start_date ? new Date(filters.start_date) : null}
              onChange={(date) => handleFilterChange('start_date', date ? format(date, 'yyyy-MM-dd') : '')}
              format="dd/MM/yyyy"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
            <DatePicker
              label="End Date"
              value={filters.end_date ? new Date(filters.end_date) : null}
              onChange={(date) => handleFilterChange('end_date', date ? format(date, 'yyyy-MM-dd') : '')}
              format="dd/MM/yyyy"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
            />
            <TextField
              select label="Show" size="small"
              value={filters.resource_type}
              onChange={(e) => handleFilterChange('resource_type', e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Rooms &amp; Desks</MenuItem>
              <MenuItem value="room">Rooms only</MenuItem>
              <MenuItem value="desk">Desks only</MenuItem>
            </TextField>
            <TextField
              select label="Status" size="small"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>
            <Button variant="contained" startIcon={<Refresh />} onClick={loadBookings} disabled={loading}>
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card>
        <CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Date</TableCell>
                  <TableCell>Day</TableCell>
                  <TableCell>Time From</TableCell>
                  <TableCell>Time Until</TableCell>
                  <TableCell>Room / Desk</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(filters.resource_type ? bookings.filter(b => b.resource_type === filters.resource_type) : bookings)
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell sx={{ p: '8px' }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: '4px', backgroundColor: booking.color || '#B082E4', border: '1px solid rgba(0,0,0,0.1)' }} />
                      </TableCell>
                      <TableCell>{formatDateUK(booking.booking_date)}</TableCell>
                      <TableCell>{getDayOfWeek(booking.booking_date)}</TableCell>
                      <TableCell>{booking.start_time || '-'}</TableCell>
                      <TableCell>{booking.end_time || '-'}</TableCell>
                      <TableCell>{booking.room_name}</TableCell>
                      <TableCell>{booking.doctor_name || '-'}</TableCell>
                      <TableCell>{booking.purpose || '-'}</TableCell>
                      <TableCell>
                        <Chip label={booking.status} color={getStatusColor(booking.status)} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" title="Edit" onClick={() => handleEdit(booking)} disabled={booking.status === 'cancelled'}>
                          <Edit fontSize="small" />
                        </IconButton>
                        {booking.series_id && (
                          <IconButton size="small" title="Modify Series" onClick={() => handleModifyClick(booking)} disabled={booking.status === 'cancelled'}>
                            <EditCalendar fontSize="small" />
                          </IconButton>
                        )}
                        {booking.series_id && (
                          <IconButton size="small" title="Extend Series" onClick={() => handleExtendClick(booking)} disabled={booking.status === 'cancelled'}>
                            <MoreTime fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" title="Cancel" onClick={() => handleDeleteClick(booking)} disabled={booking.status === 'cancelled'}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {bookings.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No bookings found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={filters.resource_type ? bookings.filter(b => b.resource_type === filters.resource_type).length : bookings.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </CardContent>
      </Card>

      {/* ── Cancel dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          {bookingToDelete && (
            <Box>
              <Typography sx={{ mb: 2 }}>Are you sure you want to cancel this booking?</Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Date:</strong> {formatDateUK(bookingToDelete.booking_date)}</Typography>
                <Typography variant="body2"><strong>Time:</strong> {bookingToDelete.start_time} – {bookingToDelete.end_time}</Typography>
                <Typography variant="body2"><strong>Room:</strong> {bookingToDelete.room_name}</Typography>
              </Box>
              <TextField
                select label="Reason for cancellation" fullWidth required
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                sx={{ mb: 2 }}
              >
                {CANCEL_REASONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </TextField>
              {bookingToDelete.series_id && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This booking is part of a recurring series. You can cancel just this single booking, or cancel this and all future bookings in the series.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Keep Booking</Button>
          {bookingToDelete?.series_id && (
            <Button onClick={handleDeleteSeries} color="error" variant="outlined" disabled={!cancelReason}>
              Cancel This &amp; Future Bookings
            </Button>
          )}
          <Button onClick={handleDeleteSingle} color="error" variant="contained" disabled={!cancelReason}>
            {bookingToDelete?.series_id ? 'Cancel This Booking Only' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modify series dialog ── */}
      <ModifySeriesDialog
        open={modifyDialogOpen}
        onClose={() => setModifyDialogOpen(false)}
        booking={bookingToModify}
        onSuccess={() => loadBookings()}
      />

      {/* ── Extend series dialog ── */}
      <Dialog open={extendDialogOpen} onClose={handleExtendCancel} maxWidth="md" fullWidth>
        <DialogTitle>Extend Recurring Series</DialogTitle>
        <DialogContent>
          {bookingToExtend && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Extend this recurring booking series by adding more bookings to the end of the series.
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 3 }}>
                <Typography variant="body2"><strong>Room:</strong> {bookingToExtend.room_name}</Typography>
                <Typography variant="body2"><strong>Time:</strong> {bookingToExtend.start_time} – {bookingToExtend.end_time}</Typography>
                {bookingToExtend.doctor_name && (
                  <Typography variant="body2"><strong>Name:</strong> {bookingToExtend.doctor_name}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                <DatePicker
                  label="New End Date"
                  value={extendEndDate}
                  onChange={(date) => { setExtendEndDate(date); setExtendPreview(null); }}
                  format="dd/MM/yyyy"
                  minDate={new Date()}
                  slotProps={{ textField: { fullWidth: true, required: true, helperText: 'Default: 12 months from today' } }}
                />
                <Button variant="outlined" onClick={handleExtendPreview} disabled={previewLoading || !extendEndDate} sx={{ mt: 1, minWidth: 120 }}>
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
              {extendPreview && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2"><strong>Current last booking:</strong> {formatDateUK(extendPreview.current_last_date)}</Typography>
                    <Typography variant="body2"><strong>New bookings to be created:</strong> {extendPreview.instances_to_create}</Typography>
                  </Alert>
                  {extendPreview.conflicts?.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {extendPreview.conflicts.length} date(s) will be skipped due to conflicts:
                      </Typography>
                      <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                        {extendPreview.conflicts.map((conflict, i) => (
                          <Typography key={i} variant="body2" sx={{ ml: 1 }}>
                            • {formatDateUK(conflict.date)} – {getDayOfWeek(conflict.date)}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}
                  {extendPreview.instances_to_create === 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>No new bookings can be created. All dates have conflicts or the end date is too soon.</Alert>
                  )}
                </Box>
              )}
              {extendError && <Alert severity="error" sx={{ mt: 2 }}>{extendError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExtendCancel} disabled={extendLoading}>Cancel</Button>
          <Button
            onClick={handleExtendConfirm} color="primary" variant="contained"
            disabled={extendLoading || !extendEndDate || !extendPreview || extendPreview.instances_to_create === 0}
          >
            {extendLoading ? 'Extending...' : `Extend Series (${extendPreview?.instances_to_create || 0} bookings)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default BookingList;
